import { useApeironSsoRedirect } from '@apeiron/auth'
import {
  ApeironBlueButton,
  useAccountContext,
  useMessageContext
} from '@apeiron/temp'
import { Box, styled, Typography } from '@mui/material'
import ApeironSwitch from '@src/components/share/apeiron/ApeironSwitch'
import { ROUTE } from '@src/constants/route'
import { useBountyContext } from '@src/contexts/share/BountyContext'
import { ChangeEvent, useCallback } from 'react'
import { useTranslation } from 'react-i18next'

const Container = styled(Box)`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  width: 95%;
  margin: auto;
  margin-bottom: 12px;
`

const Header = styled(Typography)`
  font-size: 30px;
  font-weight: 300;

  ${props => props.theme.breakpoints.down('res768')} {
    font-size: 24px;
  }
`

const PostBountyButton = styled(ApeironBlueButton)`
  width: 240px;

  ${props => props.theme.breakpoints.down('res768')} {
    width: 180px;
  }
`

const RightSide = styled(Box)`
  display: flex;
  flex-direction: row;
  align-items: center;
  flex-basis: 60%;
  justify-content: flex-end;
  column-gap: 16px;

  ${props => props.theme.breakpoints.down('res768')} {
    flex-wrap: wrap;
    row-gap: 16px;
  }
`

const DuplicateModeBox = styled(Box)`
  display: flex;
  gap: 8px;
  align-items: center;
  justify-content: flex-start;
`

const BountyHeader = ({ allowCreate = true, totalCount }: Props) => {
  const { requireAuth, isLoggedIn } = useAccountContext()

  const { setIsCreateDialogOpen, setDuplicateModeOn } = useBountyContext()

  const { setInfo } = useMessageContext()

  const { jumpToSso } = useApeironSsoRedirect()

  const { t } = useTranslation()

  const handleOnCancel = useCallback(() => {
    setInfo(null)
  }, [setInfo])

  const handleOnLogin = useCallback(() => {
    jumpToSso({
      redirectUrl: window.location.href,
      ssoHandleUrl: `${window.location.origin}${ROUTE.CALLBACK_APEIRON_SSO}`
    })
  }, [jumpToSso])

  const handleCreateClick = () => {
    if (!isLoggedIn) {
      setInfo({
        message: t('login_prompt.text'),
        cancel: t('common.cancel'),
        confirm: t('top_bar.login'),
        onConfirm: handleOnLogin,
        onCancel: handleOnCancel
      })

      return
    }

    requireAuth(() => {
      setIsCreateDialogOpen(true)
    })
  }

  const handleSwitchChange = (event: ChangeEvent<HTMLInputElement>) => {
    setDuplicateModeOn(event.target.checked)
  }

  return (
    <Container>
      <Header>
        {t('bounty.header.bounties_total', {
          total: totalCount
        })}
      </Header>
      <RightSide>
        {allowCreate && (
          <>
            <DuplicateModeBox>
              <ApeironSwitch onChange={handleSwitchChange} />
              {t('bounty.header.duplicate_bounty')}
            </DuplicateModeBox>

            <PostBountyButton onClick={handleCreateClick}>
              {t('bounty.apostle.create_dialog.post_bounty')}
            </PostBountyButton>
          </>
        )}
      </RightSide>
    </Container>
  )
}

type Props = {
  allowCreate?: boolean
  totalCount: number | undefined
}

export default BountyHeader
